import { call, put } from "typed-redux-saga";
import { AppConfig } from "../../AppConfig";
import { MenuSearchResponse } from "../../core/api/requests/menu/search/types";
import { menuLikeApi, menuSearchApi } from "../../core/http/httpClient";
import { LocalStorageManager } from "../../core/storage/LocalStorageManager";
import { ApiResponse } from "../../core/types";
import { Campaign } from "../campaign";
import { couponActions } from "../coupon";
import { takeEveryFsa } from "../operations";
import { menuActions } from "./actions";
import {
  Category,
  EarlyBirdEligible,
  Menu,
  MenuInCart,
  MenuOption,
} from "./types";

export function* menuSaga() {
  yield takeEveryFsa(menuActions.getMenus.started, function* (action) {
    const { onComplete, onError, onSuccess, ...body } = action.payload;
    const { response, error }: ApiResponse<MenuSearchResponse> = yield call(
      () => {
        return menuSearchApi
          .call(body)
          .then((response) => ({ response }))
          .catch((error) => ({ error }));
      },
    );

    onComplete?.();

    if (!response || error) return onError?.(error);

    const categories: Category[] = [];
    const campaign = LocalStorageManager.Instance.getObject<Campaign>(
      AppConfig.Instance.LocalStorageKey["campaign"],
    );

    response.data.categories.forEach((category) => {
      categories.push({
        id: category.id,
        name: category.name,
        sortOrder: category.sort_order,
        count: category.count,
        text: category.text,
        menus: category.menus.map<Menu>((menu) => ({
          id: menu.id,
          name: menu.name,
          code: menu.code,
          categoryId: category.id,
          imageUrl: menu.image,
          price: menu.price,
          priceWithTax: menu.price_with_tax,
          isFavorite: menu.favorite,
          isEnableButton: menu.enable_button,
          isPreorder: menu.preorder,
          preorderMessage: menu.preorder_message,
          isLimitedMenu: false, // Check by coupon type instead
          isRecommended: menu.recommend !== null,
          recommendOrder: menu.recommend,
          isEarlyBird: !!campaign
            ? [EarlyBirdEligible.EarlyBird, EarlyBirdEligible.Both].includes(
                menu.early_bird_eligible,
              )
            : false,
          details: menu.details,
          options: menu.option.map<MenuOption>((opt) => ({
            id: opt.id,
            name: opt.name,
          })),
          campaign: menu.campaign.price
            ? {
                price: menu.campaign.price,
                priceWithTax: menu.campaign.price_with_tax,
                details: menu.campaign.details,
              }
            : null,
        })),
      });
    });

    yield* put(
      menuActions.getMenus.done({
        params: action.payload,
        result: categories,
      }),
    );
    onSuccess?.(categories);
  });

  yield takeEveryFsa(menuActions.getMenusFromCart.started, function* (action) {
    const menusInCart: MenuInCart[] =
      LocalStorageManager.Instance.getObject(
        AppConfig.Instance.LocalStorageKey["menusInCart"],
      ) ?? [];

    const filteredMenus = menusInCart.filter((menu) => menu.amount > 0);

    LocalStorageManager.Instance.saveObject(
      AppConfig.Instance.LocalStorageKey["menusInCart"],
      filteredMenus,
    );

    yield* put(
      menuActions.getMenusFromCart.done({
        params: action.payload,
        result: filteredMenus,
      }),
    );
  });

  yield takeEveryFsa(menuActions.setMenuInCart.started, function* (action) {
    const { onComplete, onError, onSuccess, ...body } = action.payload;
    LocalStorageManager.Instance.saveObject(
      AppConfig.Instance.LocalStorageKey["menusInCart"],
      body.menus,
    );

    yield* put(
      menuActions.setMenuInCart.done({
        params: action.payload,
        result: body.menus,
      }),
    );
    onSuccess?.(body.menus);
  });

  yield takeEveryFsa(
    menuActions.addCouponMenusToCart.started,
    function* (action) {
      const { onComplete, onError, onSuccess, menus } = action.payload;
      const menusInCart: MenuInCart[] =
        LocalStorageManager.Instance.getObject(
          AppConfig.Instance.LocalStorageKey["menusInCart"],
        ) ?? [];

      menusInCart.push(...menus);

      LocalStorageManager.Instance.saveObject(
        AppConfig.Instance.LocalStorageKey["menusInCart"],
        menusInCart,
      );

      yield* put(
        menuActions.addCouponMenusToCart.done({
          params: action.payload,
          result: menusInCart,
        }),
      );
      onSuccess?.(menusInCart);
    },
  );

  yield takeEveryFsa(menuActions.addMenuToCart.started, function* (action) {
    const { onComplete, onError, onSuccess, ...body } = action.payload;
    const menusInCart: MenuInCart[] =
      LocalStorageManager.Instance.getObject(
        AppConfig.Instance.LocalStorageKey["menusInCart"],
      ) ?? [];

    const existingMenu = menusInCart.find(
      (item) =>
        item.menuId === body.menuId &&
        item.optionId === body.optionId &&
        item.couponUuid === body.couponUuid,
    );

    if (existingMenu) existingMenu.amount += body.amount;
    else
      menusInCart.push({
        menuId: body.menuId,
        menuName: body.menuName,
        amount: body.amount,
        optionId: body.optionId,
        optionName: body.optionName,
        couponCode: body.couponCode,
        isEarlyBird: body.isEarlyBird,
      });

    LocalStorageManager.Instance.saveObject(
      AppConfig.Instance.LocalStorageKey["menusInCart"],
      menusInCart,
    );

    yield* put(
      menuActions.addMenuToCart.done({
        params: action.payload,
        result: menusInCart,
      }),
    );
    onSuccess?.(menusInCart);
  });

  yield takeEveryFsa(
    menuActions.removeMenuFromCart.started,
    function* (action) {
      const { onComplete, onError, onSuccess, ...body } = action.payload;
      const menusInCart: MenuInCart[] =
        LocalStorageManager.Instance.getObject(
          AppConfig.Instance.LocalStorageKey["menusInCart"],
        ) ?? [];

      const menuToRemove = menusInCart[body.index];
      const couponUuidToRemove = menuToRemove?.couponUuid;

      menusInCart.splice(body.index, 1);

      if (couponUuidToRemove) {
        yield* put(
          couponActions.removeAppliedCoupon.started({
            couponUuids: [couponUuidToRemove],
          }),
        );
      }

      LocalStorageManager.Instance.saveObject(
        AppConfig.Instance.LocalStorageKey["menusInCart"],
        menusInCart,
      );

      yield* put(
        menuActions.removeMenuFromCart.done({
          params: action.payload,
          result: menusInCart,
        }),
      );
      onSuccess?.(menusInCart);
    },
  );

  yield takeEveryFsa(
    menuActions.removeMenuFromCartByCouponUuids.started,
    function* (action) {
      const { onComplete, onError, onSuccess, isDeleteOnce, ...body } =
        action.payload;
      const menusInCart: MenuInCart[] =
        LocalStorageManager.Instance.getObject(
          AppConfig.Instance.LocalStorageKey["menusInCart"],
        ) ?? [];

      const filteredMenus = isDeleteOnce
        ? removeOnePerCouponUuid(menusInCart, body.couponUuids)
        : menusInCart.filter(
            (menu) =>
              !(menu.couponUuid && body.couponUuids.includes(menu.couponUuid)),
          );

      LocalStorageManager.Instance.saveObject(
        AppConfig.Instance.LocalStorageKey["menusInCart"],
        filteredMenus,
      );

      yield* put(
        menuActions.removeMenuFromCartByCouponUuids.done({
          params: action.payload,
          result: filteredMenus,
        }),
      );
      onSuccess?.(filteredMenus);
    },
  );

  yield takeEveryFsa(menuActions.setFavoriteMenu.started, function* (action) {
    const { onComplete, onError, onSuccess, ...body } = action.payload;
    const { response, error }: ApiResponse<{}> = yield call(() => {
      return menuLikeApi
        .call(body)
        .then((response) => ({ response }))
        .catch((error) => ({ error }));
    });

    onComplete?.();

    if (!response || error) return onError?.(error);

    yield* put(
      menuActions.setFavoriteMenu.done({
        params: action.payload,
        result: response.data,
      }),
    );
    onSuccess?.(response.data);
  });
}

const removeOnePerCouponUuid = (
  menusInCart: MenuInCart[],
  couponUuids: string[],
): MenuInCart[] => {
  let result = [...menusInCart];

  for (const couponUuid of couponUuids) {
    const index = result.findIndex((menu) => menu.couponUuid === couponUuid);
    if (index !== -1) {
      result = [...result.slice(0, index), ...result.slice(index + 1)];
    }
  }

  return result;
};
